import { useState, useEffect, useRef, useCallback } from 'react';

const IDLE_EVENTS = ['mousemove', 'mousedown', 'touchstart', 'keydown', 'scroll'];
const TIMEOUT_MS = 15 * 60 * 1000;     // 15 minutes
const WARNING_MS = 14 * 60 * 1000;     // warning at 14 min

export default function useIdleTimer({ onIdle }) {
  const [showWarning, setShowWarning] = useState(false);
  const [remainingSeconds, setRemainingSeconds] = useState(60);

  const idleRef = useRef(null);
  const warnRef = useRef(null);
  const countRef = useRef(null);
  const warningRef = useRef(false);

  const clearAll = useCallback(() => {
    if (idleRef.current) clearTimeout(idleRef.current);
    if (warnRef.current) clearTimeout(warnRef.current);
    if (countRef.current) clearInterval(countRef.current);
  }, []);

  const reset = useCallback(() => {
    clearAll();
    warningRef.current = false;
    setShowWarning(false);
    setRemainingSeconds(60);

    warnRef.current = setTimeout(() => {
      warningRef.current = true;
      setShowWarning(true);
      const deadline = Date.now() + (TIMEOUT_MS - WARNING_MS);
      countRef.current = setInterval(() => {
        const left = Math.max(0, Math.round((deadline - Date.now()) / 1000));
        setRemainingSeconds(left);
        if (left <= 0) clearInterval(countRef.current);
      }, 1000);
    }, WARNING_MS);

    idleRef.current = setTimeout(() => {
      clearAll();
      setShowWarning(false);
      if (onIdle) onIdle();
    }, TIMEOUT_MS);
  }, [clearAll, onIdle]);

  useEffect(() => {
    const handleActivity = () => {
      if (!warningRef.current) reset();
    };
    IDLE_EVENTS.forEach(e => window.addEventListener(e, handleActivity, { passive: true }));
    reset();
    return () => {
      IDLE_EVENTS.forEach(e => window.removeEventListener(e, handleActivity));
      clearAll();
    };
  }, [reset, clearAll]);

  return { showWarning, remainingSeconds, reset };
}
